import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import config from '../config/config';

// Define user roles
export type UserRole = 'SuperAdmin' | 'OrgAdmin';

// Define user type
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  org_id?: string;
}

// Define organization type
export interface Organization {
  id: string;
  name: string;
  apps: {
    invoiceExtraction: boolean;
    documentChat: boolean;
  };
  quota: {
    pages: number;
    used: number;
  };
}

// Static users for demo
export const STATIC_USERS: User[] = [
  {
    id: '1',
    name: 'Super Admin',
    email: 'admin@docuxray.com',
    role: 'SuperAdmin'
  },
  {
    id: '2',
    name: 'Org Admin 1',
    email: 'org1@example.com',
    role: 'OrgAdmin',
    org_id: 'org1'
  },
  {
    id: '3',
    name: 'Org Admin 2',
    email: 'org2@example.com',
    role: 'OrgAdmin',
    org_id: 'org2'
  }
];

// Static organizations for demo
export const STATIC_ORGANIZATIONS: Organization[] = [
  {
    id: 'org1',
    name: 'Company One',
    apps: {
      invoiceExtraction: true,
      documentChat: true
    },
    quota: {
      pages: 1000,
      used: 675
    }
  },
  {
    id: 'org2',
    name: 'Company Two',
    apps: {
      invoiceExtraction: true,
      documentChat: false
    },
    quota: {
      pages: 500,
      used: 125
    }
  }
];

export interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean | undefined;
  getUserOrganization: () => Organization | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    } else {
      // Set to false if no stored user found
      setIsAuthenticated(false);
    }
  }, []);

  // Login function - use user data from login response
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // First try to login via API
      try {
        const response = await fetch(`${config.apiBaseUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, password })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.token && data.user) {
            // Store token and user data
            localStorage.setItem('token', data.token);
            localStorage.setItem('userData', JSON.stringify(data.user));
            localStorage.setItem('currentUser', JSON.stringify(data.user));
            
            // Store organization ID separately for easy access
            if (data.user && data.user.org_id) {
              localStorage.setItem('orgId', data.user.org_id);
            }
            
            setCurrentUser(data.user);
            setIsAuthenticated(true);
            return true;
          }
        }
      } catch (apiError) {
        console.error('API login error:', apiError);
        // Fall through to static login if API fails
      }
      
      // Get token and user data from localStorage (set during API login)
      const token = localStorage.getItem('token');
      const storedUserData = localStorage.getItem('userData');
      
      if (token && storedUserData) {
        // Use the user data that was stored during login
        const userData = JSON.parse(storedUserData);
        setCurrentUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('currentUser', JSON.stringify(userData));
        return true;
      }
      
      // Fallback to static users for demo if no stored data
      const user = STATIC_USERS.find(user => user.email.toLowerCase() === email.toLowerCase());
      
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
        localStorage.setItem('currentUser', JSON.stringify(user));
        
        // Store organization ID separately for easy access
        if (user.org_id) {
          localStorage.setItem('orgId', user.org_id);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('orgId');
  };

  // Get organization for current user
  const getUserOrganization = (): Organization | undefined => {
    if (!currentUser || !currentUser.org_id) return undefined;
    return STATIC_ORGANIZATIONS.find(org => org.id === currentUser.org_id);
  };

  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated,
    getUserOrganization
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};